import { isValidLocalDate } from '../budget/schedule';
import { compareDates, localDate, type LocalDate } from '../dates';
import type { DomainEvent } from '../events';
import { singletonId } from '../ids';
import type { Mutation } from '../mutations';
import type { BuildCtx } from '../spending/types';
import {
  isWeightUnit,
  weightMutation,
  type WeightEntryLike,
  type WeightResult,
  type WeightUnit,
} from './types';
import { isValidGrams } from './units';

export const MAX_WEIGHT_NOTE = 200;
export type WeightEntryError = 'bad_date' | 'future_date' | 'bad_weight' | 'bad_unit' | 'not_found';

/**
 * One entry per local day: the id is the user's singleton for that date, so logging a day again
 * replaces it, and two offline devices logging the same day converge on one row.
 */
export function weightEntryId(userId: string, date: LocalDate): string {
  return singletonId(userId, `weight_entries.${date}`);
}

export interface WeightInput {
  date: LocalDate;
  valueG: number;
  unit: WeightUnit;
  note?: string | null;
}

export interface LoggedWeight {
  entry: WeightEntryLike;
  mutation: Mutation;
  event: DomainEvent;
}

/**
 * Log (or replace) a day's weight. A plain insert with every field: on a live row it merges, on a
 * soft-deleted row it restores it with the new value.
 */
export function buildLogWeight(
  userId: string,
  input: WeightInput,
  ctx: BuildCtx,
): WeightResult<LoggedWeight, WeightEntryError> {
  if (!isValidLocalDate(input.date)) return { ok: false, error: 'bad_date' };
  if (compareDates(input.date, localDate(ctx.now, ctx.tz)) > 0)
    return { ok: false, error: 'future_date' };
  if (!isValidGrams(input.valueG)) return { ok: false, error: 'bad_weight' };
  if (!isWeightUnit(input.unit)) return { ok: false, error: 'bad_unit' };
  const note = (input.note ?? '').trim().replace(/\s+/g, ' ').slice(0, MAX_WEIGHT_NOTE) || null;
  const entry: WeightEntryLike = {
    id: weightEntryId(userId, input.date),
    date: input.date,
    valueG: input.valueG,
    unit: input.unit,
    note,
    deletedAt: null,
  };
  const mutation = weightMutation(ctx, 'weight_entries', 'insert', entry.id, {
    date: entry.date,
    valueG: entry.valueG,
    unit: entry.unit,
    note: entry.note,
    recordedTz: ctx.tz,
  });
  return {
    ok: true,
    entry,
    mutation,
    event: {
      type: 'weight.logged',
      entryId: entry.id,
      date: entry.date,
      valueG: entry.valueG,
      at: ctx.now,
      mutationId: mutation.id,
    },
  };
}

/** Soft-deletes a day's entry; Undo logs the same values again with buildLogWeight. */
export function buildDeleteWeight(
  entry: WeightEntryLike | null,
  ctx: BuildCtx,
): WeightResult<{ mutation: Mutation }, WeightEntryError> {
  if (!entry || entry.deletedAt !== null) return { ok: false, error: 'not_found' };
  return { ok: true, mutation: weightMutation(ctx, 'weight_entries', 'delete', entry.id) };
}
